import React, { useState } from "react";
import "./TextInput.css";
import { IoIosSearch } from "react-icons/io";

const TextInput = ({
  type,
  placeholderText,
  innerLabel,
  width,
  idName,
  inputName,
  inputValue,
  handleChange,
  inputRef,
  isRequired,
  selectOptions,
  textRows,
  accept,
  defaultValue,
  error,
  onFocus,
  readOnly,
  leftIcon,
  rightIcon,
  className,
  inputClassName,
}) => {
  const [passType, setPassType] = useState("password");

  const isAllowedControlKey = (e) => {
    const allowedKeys = [
      "Backspace",
      "Delete",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Tab",
      "Home",
      "End",
    ];
    if (allowedKeys.includes(e.key)) return true;
    if (e.ctrlKey || e.metaKey) return true; // allow shortcuts like Ctrl+C/V/X/A
    return false;
  };

  const handleNumericKeyDown = (e) => {
    if (isAllowedControlKey(e)) return;
    if (!/^\d$/.test(e.key)) {
      e.preventDefault();
    }
  };

  const handleTelChange = (e) => {
    const digitsOnly = (e.target.value || "").replace(/\D/g, "");
    if (digitsOnly !== e.target.value) {
      e.target.value = digitsOnly;
    }
    handleChange(e);
  };

  const computeNextValueAfterPaste = (pasteText) => {
    const pasteDigits = (pasteText || "").replace(/\D/g, "");
    const currentValue = inputValue || "";
    const el = inputRef && inputRef.current ? inputRef.current : null;
    if (
      el &&
      typeof el.selectionStart === "number" &&
      typeof el.selectionEnd === "number"
    ) {
      const start = el.selectionStart;
      const end = el.selectionEnd;
      return (
        currentValue.slice(0, start) + pasteDigits + currentValue.slice(end)
      );
    }
    return `${currentValue}${pasteDigits}`;
  };

  const handleTelPaste = (e) => {
    const text = e.clipboardData.getData("text");
    const digitsOnly = (text || "").replace(/\D/g, "");
    if (digitsOnly !== text) {
      e.preventDefault();
      const nextValue = computeNextValueAfterPaste(text);
      handleChange({ target: { name: inputName, value: nextValue } });
    }
  };

  return (
    <>
      {type === "select" ? (
        <select
          className={`${error ? "error" : ""} selectInput ${className || ""}`}
          id={idName}
          name={inputName}
          required={isRequired}
          value={inputValue}
          onChange={handleChange}
          ref={inputRef}
          defaultValue={defaultValue}
        >
          <option className="text-[#C4C4C4] text-sm" value="" disabled>
            {placeholderText}
          </option>
          {selectOptions.map((selectOption) => {
            return (
              <option
                className="font-medium text-sm capitalize text-[#666]"
                key={selectOption}
                value={selectOption}
              >
                {selectOption}
              </option>
            );
          })}
        </select>
      ) : type === "textbox" ? (
        <textarea
          className={`${error ? "error" : ""} textArea ${className || ""}`}
          rows={textRows}
          placeholder={placeholderText}
          required={isRequired}
          value={inputValue}
          onChange={handleChange}
          ref={inputRef}
        />
      ) : type === "file" ? (
        <div className="fileInputContainer">
          <label className="fileInputPlaceholder">{placeholderText}</label>
          <input
            className={`${error ? "error" : ""} fileInput ${className || ""}`}
            name={inputName}
            type="file"
            style={{ width: `${width}` }}
            value={inputValue}
            onChange={handleChange}
            required={isRequired}
            accept={accept}
            ref={inputRef}
          />
        </div>
      ) : type === "password" ? (
        <div className={`relative flex items-center w-full ${className || ""}`}>
          {leftIcon && (
            <div className="absolute left-3.5 flex items-center justify-center pointer-events-none text-gray-400 z-10">
              {leftIcon}
            </div>
          )}
          <input
            className={`${error ? "error" : ""} textInput ${leftIcon ? "has-left-icon !pl-11" : ""} has-right-icon !pr-11 ${inputClassName || ""}`}
            name={inputName}
            type={passType}
            style={{
              width: `${width}`,
              paddingLeft: leftIcon ? "44px" : undefined,
              paddingRight: "44px",
            }}
            placeholder={placeholderText}
            value={inputValue}
            onChange={handleChange}
            onFocus={onFocus}
            required={isRequired}
            defaultValue={defaultValue}
            ref={inputRef}
          />
          <button
            type="button"
            onClick={() =>
              passType === "password"
                ? setPassType("text")
                : setPassType("password")
            }
            className="absolute right-3.5 flex items-center justify-center text-gray-400 hover:text-gray-600 focus:outline-none z-10 transition-colors duration-150 cursor-pointer"
            tabIndex={-1}
            aria-label={passType === "password" ? "Show password" : "Hide password"}
          >
            {passType === "password" ? (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
              </svg>
            )}
          </button>
        </div>
      ) : type === "search" ? (
        <div className={`relative flex items-center w-full ${className || ""}`}>
          <input
            className={`${error ? "error" : ""} textInput pr-10 ${inputClassName || ""}`}
            name={inputName}
            type="text"
            style={{ width: `${width}` }}
            placeholder={placeholderText}
            value={inputValue}
            onChange={handleChange}
            onFocus={onFocus}
            required={isRequired}
            defaultValue={defaultValue}
            ref={inputRef}
          />
          <IoIosSearch className="absolute w-6 h-6 right-3 text-gray-600" />
        </div>
      ) : type === "tel" ? (
        <div
          className={`flex items-center w-full ${
            innerLabel ? "gap-1 bg-[#F5F5F5] rounded-lg px-3 text-sm" : ""
          } ${className || ""}`}
        >
          {innerLabel}
          <input
            className={`${error ? "error" : ""} textInput ${inputClassName || ""}`}
            name={inputName}
            type="tel"
            style={{ width: `${width}` }}
            placeholder={placeholderText}
            value={inputValue}
            onChange={handleTelChange}
            onFocus={onFocus}
            onKeyDown={handleNumericKeyDown}
            onPaste={handleTelPaste}
            inputMode="numeric"
            pattern="[0-9]*"
            required={isRequired}
            defaultValue={defaultValue}
            ref={inputRef}
          />
        </div>
      ) : (
        <div
          className={`relative flex items-center w-full ${
            innerLabel ? "gap-1 bg-[#F5F5F5] rounded-lg px-3 text-sm" : ""
          } ${className || ""}`}
        >
          {innerLabel}
          {leftIcon && (
            <div className="absolute left-3.5 flex items-center justify-center pointer-events-none text-gray-400 z-10">
              {leftIcon}
            </div>
          )}
          <input
            className={`${error ? "error" : ""} textInput ${leftIcon ? "has-left-icon !pl-11" : ""} ${rightIcon ? "has-right-icon !pr-11" : ""} ${inputClassName || ""}`}
            name={inputName}
            type={type}
            style={{
              width: `${width}`,
              paddingLeft: leftIcon ? "44px" : undefined,
              paddingRight: rightIcon ? "44px" : undefined,
            }}
            placeholder={placeholderText}
            value={inputValue}
            onChange={handleChange}
            onFocus={onFocus}
            required={isRequired}
            defaultValue={defaultValue}
            ref={inputRef}
            readOnly={readOnly}
          />
          {rightIcon && (
            <div className="absolute right-3.5 flex items-center justify-center text-gray-400 z-10">
              {rightIcon}
            </div>
          )}
        </div>
      )}
      {error && <div className="text-xs text-[#f00] mt-1">{error}</div>}
    </>
  );
};

TextInput.defaultProps = {
  type: "text",
  idName: "",
  inputName: "",
  placeholderText: "",
  width: "100%",
  handleChange: () => {},
  isRequired: false,
  selectOptions: [],
};

export default TextInput;
